import type { TypographyVariants, TypographyVariantsOptions } from '@mui/material/styles'

enum FlowFontFamily {
  INTER = `'Inter', sans-serif`,
  INSTRUMENT_SANS = `'Instrument Sans', sans-serif`
}

const displayXLarge: TypographyVariants['displayLarge'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '3.5rem',
  fontWeight: 500,
  lineHeight: 1.14,
  letterSpacing: '-0.01em'
}

const displayLarge: TypographyVariants['displayLarge'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '2.75rem',
  fontWeight: 500,
  lineHeight: 1.14,
  letterSpacing: '-0.01em'
}
const displayMedium: TypographyVariants['displayMedium'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '2.25rem',
  fontWeight: 500,
  lineHeight: 1.27,
  letterSpacing: '-0.01em'
}
const displaySmall: TypographyVariants['displaySmall'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '2rem',
  fontWeight: 500,
  lineHeight: 1.2,
  letterSpacing: '-0.01em'
}
const headlineXLarge: TypographyVariants['headlineXLarge'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '2.5rem',
  fontWeight: 500,
  lineHeight: 1.2,
  letterSpacing: '-0.01em'
}
const headlineLarge: TypographyVariants['headlineLarge'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '2rem',
  fontWeight: 500,
  lineHeight: 1.25,
  letterSpacing: '-0.01em'
}
const headlineMedium: TypographyVariants['headlineMedium'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '1.75rem',
  fontWeight: 500,
  lineHeight: 1.29,
  letterSpacing: '-0.01em'
}
const headlineSmall: TypographyVariants['headlineSmall'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '1.5rem',
  fontWeight: 500,
  lineHeight: 1.25,
  letterSpacing: '-0.01em'
}
const headlineXSmall: TypographyVariants['headlineXSmall'] = {
  fontFamily: FlowFontFamily.INSTRUMENT_SANS,
  fontSize: '1.25rem',
  fontWeight: 500,
  lineHeight: 1.3,
  letterSpacing: '-0.01em'
}
const subtitleLarge: TypographyVariants['subtitleLarge'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '1.5rem',
  fontWeight: 500,
  lineHeight: 1.33
}
const subtitleMedium: TypographyVariants['subtitleMedium'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '1.25rem',
  fontWeight: 500,
  lineHeight: 1.4
}
const subtitleSmall: TypographyVariants['subtitleSmall'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '1rem',
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.00625rem'
}
const bodyLarge: TypographyVariants['bodyLarge'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '1.125rem',
  fontWeight: 600,
  lineHeight: 1.4,
  letterSpacing: '0.15px'
}
const bodyMedium: TypographyVariants['bodyMedium'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.009375rem'
}
const bodySmall: TypographyVariants['bodySmall'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.43,
  letterSpacing: '0.00625rem'
}
const labelLarge: TypographyVariants['labelLarge'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '1rem',
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.00625rem'
}
const labelMedium: TypographyVariants['labelMedium'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '0.875rem',
  fontWeight: 500,
  lineHeight: 1.43,
  letterSpacing: '0.00625rem'
}
const labelSmall: TypographyVariants['labelSmall'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '0.75rem',
  fontWeight: 500,
  lineHeight: 1.33,
  letterSpacing: '0.03125rem'
}
const captionMedium: TypographyVariants['captionMedium'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 1.333,
  letterSpacing: '0.03125rem'
}
const overlineSmall: TypographyVariants['overlineSmall'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '0.75rem',
  fontWeight: 400,
  lineHeight: 2.67,
  letterSpacing: '0.0625rem',
  textTransform: 'uppercase'
}
const overlineMedium: TypographyVariants['overlineMedium'] = {
  fontFamily: FlowFontFamily.INTER,
  fontSize: '0.875rem',
  fontWeight: 700,
  lineHeight: 1.43,
  letterSpacing: '0.0625rem',
  textTransform: 'uppercase'
}

const variants = {
  displayXLarge,
  displayLarge,
  displayMedium,
  displaySmall,
  headlineXLarge,
  headlineLarge,
  headlineMedium,
  headlineSmall,
  headlineXSmall,
  subtitleLarge,
  subtitleMedium,
  subtitleSmall,
  bodyLarge,
  bodyMedium,
  bodySmall,
  labelLarge,
  labelMedium,
  labelSmall,
  captionMedium,
  overlineMedium,
  overlineSmall,

  // disable unused MUI built-in variants
  h1: undefined,
  h2: undefined,
  h3: undefined,
  h4: undefined,
  h5: undefined,
  h6: undefined,
  subtitle1: undefined,
  subtitle2: undefined,
  body2: undefined,
  overline: undefined,
  // set body1 to our bodyMedium, as MUI uses body1 to apply default styles
  body1: bodyMedium
}

export const typography2: TypographyVariantsOptions = {
  fontFamily: FlowFontFamily.INTER,
  ...variants
}
